import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import Lozenge from '@atlaskit/lozenge';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { htmlToADF } from '@atlassian/html-adf-transformer';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useSpaceIdentifiers } from '@confluence/space-utils/entry-points/useSpaceIdentifiers.ts';

import { SnippetEditorModal } from '../modals/SnippetEditorModal.tsx';
import { SnippetEditorModalMode } from '../snippetTypes.ts';

const messages = defineMessages({
	createBlockTemplate: {
		id: 'snippets.highlightActions.createBlockTemplate',
		defaultMessage: 'Create block template',
		description: 'Label for the create block template button in the highlight actions toolbar',
	},
	newLozenge: {
		id: 'snippets.highlightActions.newLozenge',
		defaultMessage: 'New',
		description: 'Lozenge label indicating this is a new feature',
	},
});

/**
 * Extracts HTML from the current browser selection.
 * Follows the same pattern used in highlight-actions, issue-create-side-panel,
 * and jira-work-create-extension packages.
 */
const getHTMLString = (activeSelection: Selection | null | undefined): string => {
	if (!activeSelection || activeSelection.rangeCount === 0) {
		return '';
	}

	const range = activeSelection.getRangeAt(0);
	const docFragment = range.cloneContents();
	const tempDiv = document.createElement('div');
	tempDiv.appendChild(docFragment);

	return tempDiv.innerHTML;
};

/**
 * Shared click handler logic for snippet creation from the highlight actions toolbar.
 * Extracts ADF from the selection and opens the SnippetEditorModal.
 */
const useSnippetClickHandler = (contentId: string) => {
	const { showModal } = useDialogs();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { spaceId, spaceKey } = useSpaceIdentifiers();

	return () => {
		const html = getHTMLString(window.getSelection());
		const docAdf = htmlToADF(html);

		// Strip the doc wrapper to get the inner content nodes
		const adfContent = docAdf?.content?.filter((node: unknown) => node !== undefined);
		if (!adfContent || adfContent.length === 0) {
			return;
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'highlightActionsMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createSnippet',
				type: 'ui',
				attributes: {
					selectionLength: JSON.stringify(adfContent)?.length,
				},
			},
		}).fire();

		showModal(SnippetEditorModal, {
			mode: SnippetEditorModalMode.CREATE,
			adf: docAdf,
			contentId,
			spaceId: spaceId ?? '',
			spaceKey: spaceKey ?? '',
			// editorAPI is null because we don't need it in VIEW mode
			// and this button is only present in VIEW mode.
			editorAPI: null,
		});
	};
};

type HighlightActionsSnippetButtonProps = {
	contentId: string;
};

/**
 * Icon-only button variant for use in the main highlight actions toolbar (legacy toolbar).
 */
export const HighlightActionsSnippetButton = ({
	contentId,
}: HighlightActionsSnippetButtonProps) => {
	const intl = useIntl();
	const handleClick = useSnippetClickHandler(contentId);

	return (
		<IconButton
			icon={TemplateIcon}
			appearance="subtle"
			label={intl.formatMessage(messages.createBlockTemplate)}
			isTooltipDisabled={false}
			onClick={handleClick}
		/>
	);
};

/**
 * Dropdown menu item variant for use in the "..." overflow menu (new toolbar).
 * Shows "Create block template" text with TemplateIcon and a "New" lozenge.
 */
export const HighlightActionsSnippetDropdownItem = ({
	contentId,
}: HighlightActionsSnippetButtonProps) => {
	const intl = useIntl();
	const handleClick = useSnippetClickHandler(contentId);

	return (
		<DropdownItem
			elemBefore={<TemplateIcon label="" />}
			elemAfter={<Lozenge appearance="new">{intl.formatMessage(messages.newLozenge)}</Lozenge>}
			onClick={handleClick}
		>
			{intl.formatMessage(messages.createBlockTemplate)}
		</DropdownItem>
	);
};
